import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { Empty, Loader, PageHeader, btn } from "@/components/Page";
import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";
import {
  TRIUMVIRATE_MODE,
  TriumvirateHeroPanel,
  TriumvirateSectionNav,
  RuleCard,
} from "@/components/triumvirate/TriumvirateSectionChrome";

export default function TriumvirateLadder() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [squadsRes, profileRes, leaderboardRes] = await Promise.all([
        api.get("/triumvirate/squads"),
        api.get("/triumvirate/profile"),
        api.get("/triumvirate/leaderboard"),
      ]);
      setPayload({
        squads: squadsRes.data,
        profile: profileRes.data,
        leaderboard: leaderboardRes.data,
      });
    } catch (error) {
      toast.error(apiErr(error, "Could not load the Ludus Trias ladder"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !payload) return <Loader label="Loading ladder…" />;

  const rewardConfig = payload?.squads?.reward_config || { leagues: {} };
  const selectedRewards = rewardConfig?.leagues?.overall?.placements || [];
  const leaderboardItems = payload?.leaderboard?.items || [];
  const previousWinners = payload?.leaderboard?.previous_winners || [];

  return (
    <div data-testid="triumvirate-ladder-page" className="space-y-6">
      <PageHeader
        title="Ludus Trias Ladder"
        subtitle="One live ladder for every squad in the mode, with one season rating and one set of monthly rewards."
        action={
          <div className="flex flex-wrap gap-2">
            <Link data-testid="triumvirate-ladder-archive-link" to="/ludus-trias/archive" className={btn("ghost")}>
              Season Archive
            </Link>
            <button data-testid="triumvirate-ladder-refresh-button" type="button" onClick={() => load()} className={btn("ghost")}>
              Refresh
            </button>
          </div>
        }
      />

      <TriumvirateHeroPanel
        pageKey="triumvirate-ladder"
        title="Track the one live-scored Ludus Trias race from a dedicated ladder view."
        copy="The old Rookie, Veteran, and Legends splits are gone. Every saved squad now competes on the same monthly ladder with one rating track and one top-three reward table."
        ratings={payload?.profile?.ratings || payload?.squads?.ratings}
        careerRatings={payload?.profile?.career_ratings || payload?.squads?.career_ratings}
        season={payload?.profile?.season || payload?.leaderboard?.season || payload?.squads?.season}
      />

      <TriumvirateSectionNav />

      <section className="grid gap-4 lg:grid-cols-3">
        <RuleCard testId="triumvirate-ladder-rule-order" title="Ranking order" body="The ladder sorts by season rating first, then wins, losses and squad score." />
        <RuleCard testId="triumvirate-ladder-rule-scope" title="Single ladder" body="Every valid saved squad enters the same Ludus Trias ladder. There are no league tabs, brackets or other limitations." />
        <RuleCard testId="triumvirate-ladder-rule-history" title="Season Rewards" body="TBD" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div data-testid="triumvirate-leaderboard-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-cinzel text-2xl text-amber-200">Ludus Trias Ladder</div>
                <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-ladder-badge" />
              </div>
              <div className="mt-1 text-sm text-zinc-500">{payload?.leaderboard?.season?.label || "Current season"} rankings</div>
            </div>
            <div data-testid="triumvirate-ladder-my-rank" className="text-sm text-zinc-500">Your rank: {payload?.leaderboard?.my_rank || "Unranked"}</div>
          </div>

          <div className="space-y-2" data-testid="triumvirate-leaderboard-list">
            {leaderboardItems.length === 0 ? (
              <Empty label="No ranked squads are on this ladder yet." />
            ) : (
              leaderboardItems.map((entry) => (
                <div key={entry.user_id} data-testid={`triumvirate-leaderboard-row-${entry.user_id}`} className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/45 px-3 py-2.5 rounded-sm">
                  <div>
                    <div className="text-sm text-zinc-500">#{entry.rank}</div>
                    <div className="font-cinzel text-base text-amber-200">{entry.user_name}</div>
                    <div data-testid={`triumvirate-ladder-squad-${entry.user_id}`} className="mt-1 text-[11px] uppercase tracking-widest text-zinc-400">{entry.squad_name || "Ludus Trias Squad"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-amber-300 tabular-nums">{Number(entry.rating || 0).toLocaleString()}</div>
                    <div className="text-[11px] text-zinc-500">{entry.wins || 0}W · {entry.losses || 0}L{entry.score != null ? ` · Your squad ${Number(entry.score).toLocaleString()}` : ""}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div data-testid="triumvirate-previous-winners" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-cinzel text-2xl text-amber-200">Previous Season Laurels</div>
                <div className="mt-1 text-sm text-zinc-500">{payload?.leaderboard?.previous_winners_label || "No completed season archived yet."}</div>
              </div>
              <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-ladder-winners-badge" />
            </div>
            <div className="mt-4 space-y-2">
              {previousWinners.length === 0 ? (
                <div className="text-sm text-zinc-400">Once the first monthly season closes, the top finishers from the unified ladder will appear here.</div>
              ) : (
                previousWinners.map((entry) => (
                  <div key={`${entry.rank}-${entry.user_id}`} data-testid={`triumvirate-previous-winner-${entry.rank}`} className="flex flex-wrap items-center justify-between gap-3 border border-zinc-900 bg-zinc-950/35 px-3 py-2 rounded-sm">
                    <div className="text-sm text-zinc-300">#{entry.rank} {entry.user_name}</div>
                    <div className="text-xs text-amber-300 tabular-nums">{Number(entry.rating || 0).toLocaleString()}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div data-testid="triumvirate-ladder-rewards-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-cinzel text-2xl text-amber-200">Season Rewards</div>
                <div className="mt-1 text-sm text-zinc-500">Monthly placement rewards for the unified live ladder.</div>
              </div>
              <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-ladder-rewards-badge" />
            </div>

            <div className="mt-4 space-y-3" data-testid="triumvirate-ladder-rewards-list">
              {selectedRewards.map((reward) => (
                <div key={`overall-${reward.rank}`} data-testid={`triumvirate-reward-tier-overall-${reward.rank}`} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-cinzel text-lg text-amber-200">Rank #{reward.rank}</div>
                      <div className="mt-1 text-sm text-zinc-400">{reward.title}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3 text-sm text-zinc-300">
                    <div data-testid={`triumvirate-reward-gold-overall-${reward.rank}`}>Gold: {Number(reward.gold || 0).toLocaleString()}</div>
                    <div data-testid={`triumvirate-reward-follis-overall-${reward.rank}`}>Follis: {Number(reward.follis || 0).toLocaleString()}</div>
                    <div data-testid={`triumvirate-reward-chest-overall-${reward.rank}`}>Chest: {reward.chest_item_id ? `${reward.chest_item_id} × ${reward.chest_qty}` : "None"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}